/**
 * Frontend port of management-bot-api/app/summarizer.py scoring + lambdaFilters fire buckets.
 * Project priority AirTable numbers default to 3 when unknown.
 */

/** @typedef {{ labels?: string[], milestoneDueDate?: string|null, dueDate?: string|null, assigneeKey?: string, assigneeLabel?: string, title?: string, projectLabel?: string, estimateMinutes?: number|null }} TaskLike */

export const DEFAULT_PROJECT_PRIORITY_WEIGHT = 3;

/** GitLab `Priority:` labels — also accepted on Asana if teams mirror the same text. */
export const GITLAB_STYLE_PRIORITY_LABELS = [
  "Priority: Critical",
  "Priority: High",
  "Priority: Medium",
  "Priority: Low",
  "Priority: If Time",
];

const PRIORITY_LABELS = GITLAB_STYLE_PRIORITY_LABELS;

/** True if any standard GitLab priority label is present on the task. */
export function taskHasExplicitPriorityLabel(labels) {
  const ls = Array.isArray(labels) ? labels.map((x) => String(x)) : [];
  return GITLAB_STYLE_PRIORITY_LABELS.some((p) => ls.includes(p));
}

/**
 * Replace GitLab-style priority labels with at most one new value (or none).
 * @param {string[]} currentLabels
 * @param {string} priorityLabelOrEmpty — must be one of `GITLAB_STYLE_PRIORITY_LABELS` or ""
 * @returns {string[]}
 */
export function mergeGitLabStylePriorityIntoLabels(
  currentLabels,
  priorityLabelOrEmpty
) {
  const cur = Array.isArray(currentLabels)
    ? currentLabels.map((x) => String(x))
    : [];
  const next = cur.filter((l) => !GITLAB_STYLE_PRIORITY_LABELS.includes(l));
  const p = String(priorityLabelOrEmpty || "").trim();
  if (p && GITLAB_STYLE_PRIORITY_LABELS.includes(p)) {
    next.push(p);
  }
  return next;
}

/** Mirrors summarizer priority_score weights (Unprioritized emphasized). */
const PRIORITY_WEIGHT = {
  "Priority: Unprioritized": 6,
  "Priority: Critical": 5,
  "Priority: High": 4,
  "Priority: Medium": 3,
  "Priority: Low": 2,
  "Priority: If Time": 1,
};

const LABEL_CYCLE_GENERICS = "Cycle: Generics";

/**
 * @param {string[]} labels
 */
export function gitlabPriorityLabel(labels) {
  const ls = Array.isArray(labels) ? labels.map((x) => String(x)) : [];
  for (const p of PRIORITY_LABELS) {
    if (ls.includes(p)) return p;
  }
  return "Priority: Unprioritized";
}

/**
 * Approximates AirTable `project_group` client detection (summarizer had a bug using `or`).
 * @param {TaskLike} task
 */
export function isClientWorkHeuristic(task) {
  const labels = Array.isArray(task.labels)
    ? task.labels.map((x) => String(x).toLowerCase())
    : [];
  const proj = String(task.projectLabel || "").toLowerCase();
  const title = String(task.title || "").toLowerCase();
  if (labels.some((l) => l.includes("client") && !l.includes("internal")))
    return true;
  if (/\bclient\b/i.test(proj) || /\bclient\s+work\b/i.test(title)) return true;
  if (proj.includes("me learning") || title.includes("me learning"))
    return true;
  return false;
}

/**
 * @param {TaskLike} task
 */
export function taskIsAssigned(task) {
  const k = String(task.assigneeKey || "").toLowerCase();
  if (!k || k === "unknown") return false;
  const lab = String(task.assigneeLabel || "").toLowerCase();
  if (lab.includes("not assigned")) return false;
  return true;
}

/**
 * @param {TaskLike} task
 */
export function taskIsEstimated(task) {
  const m = task.estimateMinutes;
  return Number.isFinite(m) && m > 0;
}

/**
 * management-bot `Summarizer.scoreIssue` (numeric).
 * @param {TaskLike} task
 * @param {{ projectPriorityWeight?: number }} [opts]
 */
export function scoreManagementBot(task, opts = {}) {
  const projectPriorityWeight =
    typeof opts.projectPriorityWeight === "number" &&
    Number.isFinite(opts.projectPriorityWeight)
      ? opts.projectPriorityWeight
      : DEFAULT_PROJECT_PRIORITY_WEIGHT;

  const clientWeight = isClientWorkHeuristic(task) ? 1.25 : 0.75;
  const priorityLabel = gitlabPriorityLabel(task.labels || []);
  const issuePriorityWeight =
    PRIORITY_WEIGHT[priorityLabel] ??
    PRIORITY_WEIGHT["Priority: Unprioritized"];
  const notEstimatedWeight = taskIsEstimated(task) ? 0 : 5;
  const notAssignedWeight = taskIsAssigned(task) ? 0 : 5;

  const raw =
    clientWeight * projectPriorityWeight * issuePriorityWeight +
    notEstimatedWeight +
    notAssignedWeight;

  return Math.round(raw * 100) / 100;
}

/**
 * management-bot `Summarizer.calculateFire(diff)` — diff is whole days (milestone − schedule anchor).
 * @param {number} diffDays
 * @returns {number} 0–3
 */
export function calculateFireFromDiff(diffDays) {
  if (!Number.isFinite(diffDays)) return 0;
  if (diffDays < 0) return 3;
  if (diffDays >= 0 && diffDays <= 7) return 2;
  if (diffDays > 7 && diffDays <= 14) return 1;
  return 0;
}

/**
 * Second Friday from “today” UTC — matches `pendulum.now().next(FRIDAY).next(FRIDAY)` intent.
 * @returns {string} YYYY-MM-DD
 */
export function getScheduleAnchorDateString() {
  const d = new Date();
  d.setUTCHours(12, 0, 0, 0);
  const day = d.getUTCDay();
  let addToFirstFriday = (5 - day + 7) % 7;
  if (addToFirstFriday === 0) addToFirstFriday = 7;
  d.setUTCDate(d.getUTCDate() + addToFirstFriday);
  d.setUTCDate(d.getUTCDate() + 7);
  return d.toISOString().slice(0, 10);
}

/**
 * Calendar-day delta (a − b) using UTC date parts.
 * @param {string} dateStrA YYYY-MM-DD
 * @param {string} dateStrB YYYY-MM-DD
 */
export function diffCalendarDays(dateStrA, dateStrB) {
  const pa = parseIsoDateParts(dateStrA);
  const pb = parseIsoDateParts(dateStrB);
  if (!pa || !pb) return NaN;
  const ta = Date.UTC(pa.y, pa.m - 1, pa.d);
  const tb = Date.UTC(pb.y, pb.m - 1, pb.d);
  return Math.round((ta - tb) / 86400000);
}

/**
 * @param {string|null|undefined} raw
 * @returns {{y:number,m:number,d:number}|null}
 */
function parseIsoDateParts(raw) {
  const s = String(raw || "").trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return {y: Number(m[1]), m: Number(m[2]), d: Number(m[3])};
}

/**
 * Fire 0–3: milestone due vs bot schedule anchor; generics ignore urgency; no milestone falls back to task due date.
 * @param {TaskLike} task
 */
export function managementFireLevel(task) {
  const labels = Array.isArray(task.labels)
    ? task.labels.map((x) => String(x))
    : [];
  if (labels.includes(LABEL_CYCLE_GENERICS)) {
    return calculateFireFromDiff(1000);
  }

  const anchor = getScheduleAnchorDateString();
  const milestone = task.milestoneDueDate || task.dueDate || null;

  if (!milestone) return 0;

  const diff = diffCalendarDays(String(milestone).slice(0, 10), anchor);
  return calculateFireFromDiff(diff);
}
